import React, { useEffect, useState } from "react";
import axios from "axios";
import { Download, Filter, Lock, Printer } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const currency = (value) => new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
}).format(Number(value || 0));
const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => `${today().slice(0, 8)}01`;

const FILTER_DEFAULTS = {
  start_date: monthStart(),
  end_date: today(),
  cost_center_id: "all",
  category: "all",
  transaction_type: "all",
  status: "all",
};

export const FinancialReportView = ({ costCenters = [] }) => {
  const [filters, setFilters] = useState(FILTER_DEFAULTS);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadReport = async (nextFilters = filters) => {
    setLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries(nextFilters).filter(([, value]) => value && value !== "all")
      );
      const { data } = await axios.get(`${API}/reports/balancete`, { params });
      setReport(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao carregar o balanço consolidado.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport(FILTER_DEFAULTS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilter = (name, value) => setFilters((current) => ({ ...current, [name]: value }));

  const exportCSV = () => {
    if (!report) return;
    const rows = [
      ["Balanço Consolidado — dados agregados e anônimos"],
      ["Categoria", "Subcategoria", "Entradas", "Saídas", "Saldo", "Lançamentos", "%"],
      ...(report.by_subcategory || []).map((row) => [
        row.category, row.subcategory, row.receita, row.despesa, row.saldo, row.count, row.share_pct,
      ]),
    ];
    const csv = rows.map((row) => row.map((value) => `"${value ?? ""}"`).join(";")).join("\n");
    const url = URL.createObjectURL(new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `balanco-consolidado-${filters.start_date}-${filters.end_date}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("CSV anônimo exportado.");
  };

  const exportPDF = () => {
    if (!report) return;
    try {
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const periodStart = filters.start_date?.split("-").reverse().join("/") || "Início";
      const periodEnd = filters.end_date?.split("-").reverse().join("/") || "Hoje";

      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.text("Balanço Consolidado", 40, 42);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      doc.text(`Período: ${periodStart} a ${periodEnd}`, 40, 60);
      doc.text("Relatório anônimo: sem nomes, CIM, contatos ou descrições individuais.", 40, 74);

      autoTable(doc, {
        startY: 92,
        theme: "grid",
        styles: { font: "helvetica", fontSize: 9, cellPadding: 6 },
        headStyles: { fillColor: [15, 23, 42], textColor: 255 },
        head: [["Saldo anterior", "Entradas", "Saídas", "Saldo final", "Lançamentos"]],
        body: [[
          currency(report.opening?.saldo),
          currency(report.period_totals?.receita),
          currency(report.period_totals?.despesa),
          currency(report.ending?.saldo),
          report.period_totals?.transaction_count || 0,
        ]],
      });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 18,
        theme: "striped",
        styles: { font: "helvetica", fontSize: 8, cellPadding: 5 },
        headStyles: { fillColor: [15, 23, 42], textColor: 255 },
        columnStyles: {
          2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" },
          5: { halign: "right" }, 6: { halign: "right" },
        },
        head: [["Categoria", "Subcategoria", "Entradas", "Saídas", "Saldo", "Qtd.", "%"]],
        body: (report.by_subcategory || []).map((row) => [
          row.category,
          row.subcategory,
          currency(row.receita),
          currency(row.despesa),
          currency(row.saldo),
          row.count,
          `${row.share_pct}%`,
        ]),
        didDrawPage: ({ pageNumber }) => {
          doc.setFontSize(8);
          doc.setTextColor(110, 110, 110);
          doc.text(`Lojas Integradas • Página ${pageNumber}`, 802, 575, { align: "right" });
        },
      });

      doc.save(`balanco-consolidado-${filters.start_date}-${filters.end_date}.pdf`);
      toast.success("PDF anônimo baixado com sucesso.");
    } catch (error) {
      toast.error("Não foi possível gerar o PDF.");
    }
  };

  const totals = report?.period_totals || {};
  const metrics = [
    ["Saldo anterior", report?.opening?.saldo],
    ["Entradas", totals.receita],
    ["Saídas", totals.despesa],
    ["Saldo final", report?.ending?.saldo],
  ];

  return (
    <section className="space-y-5 print:text-black" data-testid="consolidated-balance-view">
      <header className="luxury-card p-5 space-y-4 print:border print:border-slate-300">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-rose-400 font-bold">Tesouraria</p>
            <h1 className="text-3xl font-serif font-bold text-white print:text-black">Balanço Consolidado</h1>
            <div className="mt-2 flex items-center gap-2 text-xs text-slate-300 print:text-black" data-testid="anonymous-report-indicator">
              <Lock className="h-4 w-4 text-emerald-400" />
              Relatório anônimo — sem nomes, CIM ou descrições individuais.
            </div>
          </div>
          <div className="flex gap-2 print:hidden">
            <button data-testid="consolidated-pdf-button" onClick={exportPDF} disabled={!report} className="border border-slate-700 px-3 py-2 rounded-lg text-xs text-white hover:border-slate-500 disabled:opacity-50 transition-colors">
              <Printer className="inline h-4 w-4 mr-1" /> Baixar PDF
            </button>
            <button data-testid="consolidated-csv-button" onClick={exportCSV} disabled={!report} className="bg-[#D4AF37] px-3 py-2 rounded-lg text-xs font-bold text-slate-950 disabled:opacity-50 hover:bg-[#C5A028] transition-colors">
              <Download className="inline h-4 w-4 mr-1" /> CSV
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 print:hidden" data-testid="consolidated-filters">
          <FilterField label="Início"><input data-testid="balance-start-date" type="date" value={filters.start_date} onChange={(e) => updateFilter("start_date", e.target.value)} className="filter-control" /></FilterField>
          <FilterField label="Fim"><input data-testid="balance-end-date" type="date" value={filters.end_date} onChange={(e) => updateFilter("end_date", e.target.value)} className="filter-control" /></FilterField>
          <FilterField label="Centro de custo">
            <select data-testid="balance-cost-center-filter" value={filters.cost_center_id} onChange={(e) => updateFilter("cost_center_id", e.target.value)} className="filter-control">
              <option value="all">Todos</option>
              {costCenters.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </FilterField>
          <FilterField label="Categoria">
            <select data-testid="balance-category-filter" value={filters.category} onChange={(e) => updateFilter("category", e.target.value)} className="filter-control">
              <option value="all">Todas</option>
              {(report?.options?.categories || []).map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </FilterField>
          <FilterField label="Tipo">
            <select data-testid="balance-type-filter" value={filters.transaction_type} onChange={(e) => updateFilter("transaction_type", e.target.value)} className="filter-control">
              <option value="all">Todos</option><option value="receita">Entradas</option><option value="despesa">Saídas</option>
            </select>
          </FilterField>
          <FilterField label="Situação">
            <select data-testid="balance-status-filter" value={filters.status} onChange={(e) => updateFilter("status", e.target.value)} className="filter-control">
              <option value="all">Todas</option>
              {(report?.options?.statuses || []).map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </FilterField>
        </div>
        <button data-testid="balance-apply-filters-button" onClick={() => loadReport()} disabled={loading} className="print:hidden inline-flex items-center gap-2 bg-rose-500 hover:bg-rose-400 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-xs font-bold transition-colors">
          <Filter className="h-4 w-4" /> {loading ? "Carregando..." : "Aplicar filtros"}
        </button>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-700 border border-slate-700" data-testid="balance-summary">
        {metrics.map(([label, value]) => (
          <div key={label} className="bg-[#101827] p-4 print:bg-white" data-testid={`balance-metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
            <p className="text-[10px] uppercase tracking-widest text-slate-400">{label}</p>
            <p className="mt-1 text-xl font-mono font-bold text-white print:text-black">{currency(value)}</p>
          </div>
        ))}
      </div>

      <div className="luxury-card p-5 overflow-hidden">
        <h2 className="text-lg font-serif font-bold text-white print:text-black mb-3">Categorias e subcategorias</h2>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[680px] text-sm" data-testid="consolidated-category-table">
            <thead><tr className="border-b border-slate-700 text-left text-[10px] uppercase tracking-widest text-slate-400">
              <th className="py-2 pr-3">Categoria</th><th className="py-2 pr-3">Subcategoria</th><th className="py-2 px-3 text-right">Entradas</th><th className="py-2 px-3 text-right">Saídas</th><th className="py-2 px-3 text-right">Saldo</th><th className="py-2 px-3 text-right">Qtd.</th><th className="py-2 pl-3 text-right">%</th>
            </tr></thead>
            <tbody>
              {(report?.by_subcategory || []).map((row, index) => (
                <tr key={row.key} data-testid={`consolidated-category-row-${index}`} className="border-b border-slate-800 text-slate-200 hover:bg-slate-800/40 transition-colors print:text-black">
                  <td className="py-2 pr-3 font-semibold">{row.category}</td><td className="py-2 pr-3 text-slate-400 print:text-black">{row.subcategory}</td><td className="py-2 px-3 text-right font-mono text-emerald-400">{currency(row.receita)}</td><td className="py-2 px-3 text-right font-mono text-rose-400">{currency(row.despesa)}</td><td className="py-2 px-3 text-right font-mono font-bold">{currency(row.saldo)}</td><td className="py-2 px-3 text-right">{row.count}</td><td className="py-2 pl-3 text-right">{row.share_pct}%</td>
                </tr>
              ))}
              {!loading && !(report?.by_subcategory || []).length && <tr><td colSpan="7" className="py-8 text-center text-slate-500" data-testid="consolidated-empty-state">Nenhum lançamento no período.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <style>{`.filter-control{width:100%;margin-top:.25rem;background:#080c14;border:1px solid #334155;border-radius:.5rem;padding:.5rem .65rem;color:#fff;font-size:.75rem;outline:none}.filter-control:focus{border-color:#D4AF37}@media print{button,.print\\:hidden{display:none!important}}`}</style>
    </section>
  );
};

const FilterField = ({ label, children }) => (
  <label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{label}{children}</label>
);